import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  CssBaseline,
  ThemeProvider,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Icon } from "@iconify/react";
import { useMsal } from "@azure/msal-react";

import Sidebar from "./components/Sidebar";
import ChatArea from "./components/ChatArea";
import theme from "./utils/theme";
import lightTheme from "./utils/lightTheme";
import { loginRequest } from "./utils/auth/msalConfig";
import AdminPortal from "./components/AdminPortal"; // Import AdminPortal component
import { apiSessionManager } from "./utils/apiSessionManager";
import VersionUpdateNotification from "./components/VersionUpdateNotification";
import { validateEnvironment, logConfiguration } from "./config/environment";
import useVersionChecker from "./hooks/useVersionChecker";

// Validate environment on app startup
if (true) {
  validateEnvironment();
  logConfiguration();
}

const SESSION_CONTEXT_KEY = "parts360_session_context";
const CURRENT_SESSION_KEY = "parts360_current_session";

// ChatController component to handle URL parameters and navigation
function ChatController({ sessions, setSessions, sessionCounter, setSessionCounter, isSmallScreen, isTablet, mode, setMode, sessionsLoading, onChatLoadingChange }) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const [activeId, setActiveId] = useState(sessions[0]?.id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isSessionLoading, setIsSessionLoading] = useState(false); // State for session loading
  const [pendingSessionId, setPendingSessionId] = useState(null);
  const [pendingNewSession, setPendingNewSession] = useState(false);
  const chatAreaRef = React.useRef();

  // Track if ChatArea is loading
  const [isChatLoading, setIsChatLoading] = useState(false);

  const [sessionContext, setSessionContext] = useState(() => {
    // Try to load session context from sessionStorage (temporary UI state)
    try {
      const savedContext = sessionStorage.getItem(SESSION_CONTEXT_KEY);
      return savedContext ? JSON.parse(savedContext) : [];
    } catch (error) {
      console.warn('Failed to load session context:', error);
      return [];
    }
  });

  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const userName = accounts.length > 0 ? accounts[0].name || accounts[0].username : "";

  // Version checker hook
  const {
    updateAvailable,
    serverVersion,
    currentVersion,
    notificationOpen,
    handleReload,
    handleCloseNotification,
    performVersionCheck,
    isChecking
  } = useVersionChecker();



  // Save session context to sessionStorage when it changes (temporary UI state)
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_CONTEXT_KEY, JSON.stringify(sessionContext));
    } catch (error) {
      console.warn('Failed to save session context:', error);
    }
  }, [sessionContext]);

  // Handle theme toggle
  const handleThemeToggle = () => {
    setMode(prevMode => prevMode === "light" ? "dark" : "light");
  };

  // Handle sign in with Microsoft AD - improved to handle exceptions
  const handleSignIn = async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error("Sign in failed:", error);
    }
  };
  // Handle sign out - use our improved method
  const handleSignOut = () => {
    try {
      // Clear session storage and localStorage
      sessionStorage.clear();
      localStorage.removeItem(CURRENT_SESSION_KEY);
      
      // Log out using MSAL
      instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error("Sign out failed:", error);
      // If logout fails, force reload as fallback
      window.location.reload();
    }
  };  useEffect(() => {
    // Don't make navigation decisions while sessions are still loading
    if (sessionsLoading) {
      return;
    }

    // Check if we have a conversation ID in the URL
    const conversationId = params.id;

    // Store current session in localStorage for reload persistence
    if (conversationId && sessions.find(s => s.id === conversationId)) {
      try {
        localStorage.setItem(CURRENT_SESSION_KEY, conversationId);
      } catch (error) {
        console.warn('Failed to save current session to localStorage:', error);
      }
    }

    // If conversationId exists and matches a session, set it as active
    if (conversationId && sessions.find(s => s.id === conversationId)) {
      if (activeId !== conversationId) {
        console.log(`Setting active session from URL: ${conversationId}`);
        setActiveId(conversationId);
      }
    }
    // If conversationId doesn't exist or doesn't match any session
    else {
      // Try to get the last active session from localStorage first
      let fallbackSessionId = null;
      try {
        const storedSessionId = localStorage.getItem(CURRENT_SESSION_KEY);
        if (storedSessionId && sessions.find(s => s.id === storedSessionId)) {
          fallbackSessionId = storedSessionId;
          console.log('Using stored session from localStorage:', fallbackSessionId);
        }
      } catch (error) {
        console.warn('Failed to read stored session from localStorage:', error);
      }

      // If we have a fallback session, navigate to it
      if (fallbackSessionId && activeId !== fallbackSessionId) {
        console.log(`Navigating to stored session: ${fallbackSessionId}`);
        navigate(`/conversation/${fallbackSessionId}`, { replace: true });
      }
      // If there are sessions available, navigate to the first one
      else if (sessions.length > 0 && (!activeId || activeId !== sessions[0].id)) {
        console.log(`Navigating to first session: ${sessions[0].id}`);
        navigate(`/conversation/${sessions[0].id}`, { replace: true });
      } else if (sessions.length === 0) {
        // If no sessions exist, create a new one using API session manager
        const newSession = apiSessionManager.createTempSession(isAuthenticated ? userName : "");
        
        console.log(`Creating new session: ${newSession.id}`);
        setSessions([newSession]);
        setActiveId(newSession.id);
        navigate(`/conversation/${newSession.id}`, { replace: true });
      }
    }
  }, [params.id, sessions, navigate, isAuthenticated, userName, activeId, sessionsLoading]);// Load session details when navigating to a session without messages
  useEffect(() => {
    const loadSessionDetails = async () => {
      if (activeId && !activeId.startsWith('temp-') && isAuthenticated) {
        const currentSession = sessions.find(s => s.id === activeId);
        
        // If session exists but has no messages and hasn't failed loading before, try to load details from API
        if (currentSession && 
            (!currentSession.messages || currentSession.messages.length === 0) && 
            !currentSession.loadingFailed) {
          try {
            setIsSessionLoading(true); // Start loading
            
            // Get authentication token first
            const token = await apiSessionManager.getAuthToken(instance, accounts, loginRequest);
            
            if (token) {
              const sessionDetails = await apiSessionManager.getSessionDetails(activeId, token);
              
              if (sessionDetails && sessionDetails.messages && sessionDetails.messages.length > 0) {
                setSessions(prev => prev.map(s => 
                  s.id === activeId ? {
                    ...sessionDetails,
                    // Preserve original date fields from the session list
                    createdAt: s.createdAt || sessionDetails.createdAt,
                    lastModified: s.lastModified || sessionDetails.lastModified
                  } : s
                ));
              } else {
                // Mark session as failed to load to prevent retry loops
                setSessions(prev => prev.map(s => 
                  s.id === activeId ? { ...s, loadingFailed: true } : s
                ));
              }
            } else {
              console.warn('Failed to get authentication token for session details');
              // Mark session as failed to load
              setSessions(prev => prev.map(s => 
                s.id === activeId ? { ...s, loadingFailed: true } : s
              ));
            }
          } catch (error) {
            console.warn('Failed to load session details:', error);
            
            // If it's a server error, navigate to a new session instead of showing broken session
            if (error.message.includes('SESSION_ERROR')) {
              console.log('Session has server error, creating new session');
              const newSession = apiSessionManager.createTempSession(userName);
              setSessions(prev => [newSession, ...prev.filter(s => s.id !== activeId)]);
              setActiveId(newSession.id);
              navigate(`/conversation/${newSession.id}`, { replace: true });
              try {
                localStorage.setItem(CURRENT_SESSION_KEY, newSession.id);
              } catch (error) {
                console.warn('Failed to save new session to localStorage:', error);
              }
            } else {
              // Mark session as failed to load to prevent retry loops
              setSessions(prev => prev.map(s => 
                s.id === activeId ? { ...s, loadingFailed: true } : s
              ));
            }
          } finally {
            setIsSessionLoading(false); // End loading regardless of success/failure
          }
        }
      }
    };

    loadSessionDetails();
  }, [activeId, sessions, isAuthenticated, accounts, instance, userName, navigate]);

  // Handler for ChatArea to notify loading state
  const handleChatLoadingChange = (loading) => {
    setIsChatLoading(loading);
    // Also notify parent component about loading state
    if (onChatLoadingChange) {
      onChatLoadingChange(loading);
    }
  };

  // Handler for ChatArea to notify session switch dialog decision
  const handleSessionSwitchDecision = (action, sessionId) => {
    setPendingSessionId(null);
    setPendingNewSession(false);
    if (action === 'switch') {
      if (sessionId) {
        doSelectSession(sessionId, true);
      } else {
        doNewSession(true);
      }
    }
    // else 'continue' - do nothing
  };

  // Wrapped new session handler
  const doNewSession = (force = false) => {
    if (!isAuthenticated) {
      handleSignIn();
      return;
    }
    if (!force && isChatLoading) {
      setPendingNewSession(true);
      setPendingSessionId(null);
      return;
    }
    const newSession = apiSessionManager.createTempSession(userName);
    setSessions((prev) => [newSession, ...prev]);
    setActiveId(newSession.id);
    try {
      localStorage.setItem(CURRENT_SESSION_KEY, newSession.id);
    } catch (error) {
      console.warn('Failed to save new session to localStorage:', error);
    }
    setSessionContext([]);
    setTimeout(() => {
      navigate(`/conversation/${newSession.id}`);
    }, 0);
    if (isSmallScreen) setMobileOpen(false);
  };

  // Wrapped select session handler
  const doSelectSession = (id, force = false) => {
    if (!isAuthenticated) {
      handleSignIn();
      return;
    }
    if (!force && isChatLoading) {
      setPendingSessionId(id);
      setPendingNewSession(false);
      return;
    }
    const selectedSession = sessions.find(s => s.id === id);
    if (!selectedSession) {
      console.error(`Session with ID ${id} not found`);
      return;
    }
    if (activeId === id) {
      if (isSmallScreen) setMobileOpen(false);
      return;
    }
    setActiveId(id);
    try {
      localStorage.setItem(CURRENT_SESSION_KEY, id);
    } catch (error) {
      console.warn('Failed to save selected session to localStorage:', error);
    }
    setSessionContext([]);
    navigate(`/conversation/${id}`, { replace: true });
    if (isSmallScreen) setMobileOpen(false);
  };

  // Pass these handlers to Sidebar
  const handleSidebarNewSession = () => doNewSession();
  const handleSidebarSelectSession = (id) => doSelectSession(id);

  // Get current active session's messages
  const activeSessionMessages = sessions.find((s) => s.id === activeId)?.messages || [];

   const handleAppendMessage = async (msg) => {
    if (!isAuthenticated) {
      // If user isn't authenticated, trigger login
      try {
        await instance.loginPopup(loginRequest);
        // After successful authentication, continue with sending the message
        appendMessageAfterAuth(msg);
      } catch (error) {
        console.error("Authentication failed:", error);
      }
      return;
    }

    appendMessageAfterAuth(msg);
  };  const appendMessageAfterAuth = (msg) => {
    console.log("Message received:", msg);
    console.log("Message details:", {
      sender: msg.sender,
      session_name: msg.session_name,
      session_id: msg.session_id,
      hasSessionName: !!msg.session_name
    });

    // Extract session context updates to handle outside setSessions
    let sessionContextUpdate = null;
    let activeIdUpdate = null;
    let navigationUpdate = null;

    setSessions((prev) => {
      const updatedSessions = prev.map((s) => {
        if (s.id === activeId) {
          // Update the createdBy field with the current user name if it's empty
          const updatedSession = {
            ...s,
            createdBy: s.createdBy || userName
          };          // Check if we're updating an existing message (replacing loading with response)
          const existingMsgIndex = updatedSession.messages.findIndex(m => m.id === msg.id);

          console.log("DEBUG onSendMessage: Looking for existing message with ID:", msg.id);
          console.log("DEBUG onSendMessage: Current messages in session:", updatedSession.messages.map(m => ({ id: m.id, sender: m.sender, isLoading: m.isLoading })));
          console.log("DEBUG onSendMessage: Found existing message at index:", existingMsgIndex);

          if (existingMsgIndex !== -1) {
            console.log("DEBUG onSendMessage: Replacing existing message at index", existingMsgIndex);
            // Replace the existing message
            const updatedMessages = [...updatedSession.messages];
            updatedMessages[existingMsgIndex] = msg;

            // Check if this is a new session scenario (backend returned different session_id)
            const isNewSessionFromBackend = msg.sender === 'ai' && msg.session_id && 
              msg.session_id !== s.id && !s.id.startsWith('temp-');            // If this is an AI response with session_id and if we're using a temp ID
            if (msg.sender === 'ai' && msg.session_id && s.id.startsWith('temp-')) {
              console.log("Updating session ID from temp to backend ID:", msg.session_id);
              
              // Update API session manager cache
              const realSession = apiSessionManager.replaceSessionId(s.id, msg.session_id, msg.session_name);
              
              // Schedule session context update
              if (msg.sessionContext) {
                sessionContextUpdate = msg.sessionContext;
              }

              // Schedule active ID update
              activeIdUpdate = msg.session_id;

              // Schedule navigation update
              navigationUpdate = msg.session_id;

              // Update localStorage with real session ID
              try {
                localStorage.setItem(CURRENT_SESSION_KEY, msg.session_id);
              } catch (error) {
                console.warn('Failed to update session in localStorage:', error);
              }

              // Return the updated session with real ID
              return realSession || {
                ...s,
                id: msg.session_id,
                title: msg.session_name || s.title,
                messages: updatedMessages
              };
            }

            // Handle case where backend returned a completely new session_id (new conversation)
            else if (isNewSessionFromBackend) {
              console.log("Backend started new conversation - but continuing with existing session:", {
                oldSession: s.id,
                newSession: msg.session_id
              });

              // Schedule session context update for new conversation
              if (msg.sessionContext) {
                sessionContextUpdate = msg.sessionContext;
              }

              // Just continue with existing session instead of creating new one
              if (msg.sender === 'ai' && msg.session_name) {
                console.log('Updating session title (new session scenario):', {
                  sessionId: s.id,
                  oldTitle: updatedSession.title,
                  newTitle: msg.session_name
                });
              }
              
              const sessionToUpdate = { 
                ...updatedSession, 
                messages: updatedMessages,
                ...(msg.sender === 'ai' && msg.session_name && { title: msg.session_name })
              };
              
              // Update API session manager cache
              apiSessionManager.updateSessionInCache(s.id, sessionToUpdate);
              
              return sessionToUpdate;
            }

            // Schedule session context update if available
            if (msg.sender === 'ai' && msg.sessionContext) {
              sessionContextUpdate = msg.sessionContext;
            }

            // Update session title if backend provided one
            if (msg.sender === 'ai' && msg.session_name) {
              console.log('Updating session title:', {
                sessionId: s.id,
                oldTitle: updatedSession.title,
                newTitle: msg.session_name
              });
            }
            
            const sessionToUpdate = { 
              ...updatedSession, 
              messages: updatedMessages,
              ...(msg.sender === 'ai' && msg.session_name && { title: msg.session_name })
            };
            apiSessionManager.updateSessionInCache(s.id, sessionToUpdate);

            return sessionToUpdate;
          } else {
            // For new user messages, schedule session context update
            if (msg.sender === 'user') {
              sessionContextUpdate = (prev) => [
                ...prev,
                {
                  role: 'user',
                  content: msg.text,
                  timestamp: new Date().toISOString()
                }
              ];
            }

            // Add new message to session
            const sessionToUpdate = { ...updatedSession, messages: [...updatedSession.messages, msg] };
            
            // Update API session manager cache
            apiSessionManager.updateSessionInCache(s.id, sessionToUpdate);

            return sessionToUpdate;
          }
        }
        return s;
      });

      // Handle new sessions created by backend
      const aiMessageWithSessionId = msg.sender === 'ai' && msg.session_id;
      const currentSession = prev.find(s => s.id === activeId);
      const usingTempId = currentSession?.id.startsWith('temp-');
      const isNewSessionFromBackend = aiMessageWithSessionId && 
        currentSession && !usingTempId && msg.session_id !== currentSession.id;

      if (aiMessageWithSessionId && usingTempId) {
        console.log("Found session_id in AI message for temp session - updating to real session:", msg.session_id);
        // This is handled above in the session mapping
      }

      return updatedSessions;
    });

    // Apply state updates after setSessions completes
    if (sessionContextUpdate !== null) {
      if (typeof sessionContextUpdate === 'function') {
        setSessionContext(sessionContextUpdate);
      } else {
        setSessionContext(sessionContextUpdate);
      }
    }

    if (activeIdUpdate !== null) {
      setActiveId(activeIdUpdate);
    }

    if (navigationUpdate !== null) {
      setTimeout(() => {
        console.log("Navigating to new URL:", `/conversation/${navigationUpdate}`);
        navigate(`/conversation/${navigationUpdate}`, { replace: true });
      }, 100);
    }
  };
  
  const activeSession = sessions.find((s) => s.id === activeId);
  const sessionLoadingFailed = activeSession?.loadingFailed || false;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <>
      {/* Version update notification */}
      <VersionUpdateNotification
        open={notificationOpen}
        onClose={handleCloseNotification}
        onReload={handleReload}
        serverVersion={serverVersion}
        currentVersion={currentVersion}
        isQueryInProgress={isChatLoading}
      />
      
      {/* Dark mode toggle for desktop and tablet */}
      {(!isSmallScreen) && (
        <Box
          sx={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 1200,
            bgcolor: "background.paper",
            borderRadius: '50%',
            boxShadow: 3,
            transition: "background-color 0.3s",
          }}
        >
          <IconButton
            onClick={handleThemeToggle}
            sx={{
              color: "#2065D1",
              p: 1.5,
              '&:hover': {
                bgcolor: (theme) => alpha("#2065D1", 0.1),
              },
            }}
          >
            <Icon
              icon={
                mode === "dark"
                  ? "iconamoon:mode-light-duotone"
                  : "tdesign:mode-dark-filled"
              }
              width={24}
              height={24}
            />
          </IconButton>
        </Box>
      )}

      {/* Sidebar toggle for desktop and tablet */}
      {(!isSmallScreen && !sidebarOpen) && (
        <IconButton
          onClick={handleSidebarToggle}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1200,
            bgcolor: 'background.paper',
            boxShadow: 3,
            borderRadius: '50%',
            p: 1,
            '&:hover': {
              bgcolor: (theme) => alpha("#2065D1", 0.1),
            },
          }}
        >
          {sidebarOpen ? <ChevronLeftIcon sx={{ color: "#2065D1" }} /> : <Icon color="#2065D1" icon="hugeicons:sidebar-right-01" width="25px" height="25px" />}
        </IconButton>
      )}

      {/* AppBar for mobile only */}
      {isSmallScreen && (
        <AppBar
          position="fixed"
          elevation={1}
          sx={{ bgcolor: "background.default", color: "text.primary", transition: "background-color 0.3s" }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <IconButton edge="start" sx={{ color: "#2065D1" }} onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '150px',
                flex: 1,
                textAlign: 'center'
              }}
            >
              {sessions.find(s => s.id === activeId)?.title || "Parts360"}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleThemeToggle}
                sx={{
                  color: "#2065D1",
                }}
              >
                <Icon
                  icon={
                    mode === "dark"
                      ? "iconamoon:mode-light-duotone"
                      : "tdesign:mode-dark-filled"
                  }
                  width={20}
                  height={20}
                />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                endIcon={<Icon icon="fluent:person-24-regular" />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  borderColor: "#2065D1",
                  color: "#2065D1",
                  '&:hover': {
                    bgcolor: (theme) => alpha("#2065D1", 0.1),
                  },
                }}
                onClick={isAuthenticated ? handleSignOut : handleSignIn}
                disabled={inProgress !== "none"}
              >
                {isAuthenticated ? "Sign out" : "Sign in"}
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      )}
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100%",
          bgcolor: "background.default",
          color: "text.primary",
          transition: "background-color 0.3s, color 0.3s",
        }}
      >
        <Box
          sx={{
            flex: "1 1 0%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatArea
            ref={chatAreaRef}
            isMobile={isSmallScreen}
            messages={activeSessionMessages}
            onSendMessage={handleAppendMessage}
            isAuthenticated={isAuthenticated}
            handleSignIn={handleSignIn}
            isSessionLoading={isSessionLoading}
            sessionLoadingFailed={sessionLoadingFailed}
            pendingSessionId={pendingSessionId}
            pendingNewSession={pendingNewSession}
            onSessionSwitchDecision={handleSessionSwitchDecision}
            onLoadingChange={handleChatLoadingChange}
          />
        </Box>

        {/* SIDEBAR: fixed 300px wide for tablet and desktop */}
        {(!isSmallScreen && sidebarOpen) && (
          <Box
            sx={{
              flex: "0 0 300px",    // don't grow or shrink
              display: "block",     // Always show on tablet and desktop
              width: "300px",
            }}
          >
            <Sidebar
              sessions={sessions}
              activeId={activeId}
              onNewSession={handleSidebarNewSession}
              onSelectSession={handleSidebarSelectSession}
              sx={{ width: 300, bgcolor: "background.paper", boxShadow: 2 }}
              isMobile={isSmallScreen}
              handleSidebarToggle={handleSidebarToggle}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isAuthenticated={isAuthenticated}
              handleSignIn={handleSignIn}
              userName={userName}
              versionChecker={{ 
                performVersionCheck, 
                isChecking, 
                updateAvailable, 
                currentVersion, 
                serverVersion, 
                handleReload 
              }}
              isChatLoading={isChatLoading}
            />
          </Box>
        )}

        {/* MOBILE DRAWER */}
        {isSmallScreen && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: 300,
                bgcolor: "background.paper",
                boxShadow: 2,
              },
            }}
          >
            <Sidebar
              sessions={sessions}
              activeId={activeId}
              onNewSession={handleSidebarNewSession}
              onSelectSession={handleSidebarSelectSession}
              isMobile={isSmallScreen}
              handleSidebarToggle={handleSidebarToggle}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              isAuthenticated={isAuthenticated}
              handleSignIn={handleSignIn}
              userName={userName}
              versionChecker={{ 
                performVersionCheck, 
                isChecking, 
                updateAvailable, 
                currentVersion, 
                serverVersion, 
                handleReload 
              }}
              isChatLoading={isChatLoading}
            />
          </Drawer>
        )}
      </Box>
    </>
  );
}

// AdminRouter component to handle admin-specific routing
function AdminRouter({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();


  useEffect(() => {
    // Set admin flag as soon as the component mounts
    console.log("AdminRouter: Setting admin route flag");
    sessionStorage.setItem('current_route', 'admin');

    // Listen for popstate events which happen during browser back/forward navigation
    const handlePopState = () => {
      if (location.pathname === '/admin') {
        console.log("AdminRouter: Detected popstate on admin page, reinforcing flag");
        sessionStorage.setItem('current_route', 'admin');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname]);

  return (
    <AdminController mode={mode} />
  );
}

// AdminController component to handle admin route
function AdminController({ mode }) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('AdminController: Checking authentication and admin status');
    
    // Check authentication on component mount
    if (!isAuthenticated) {
      console.log("User not authenticated, redirecting to main page");
      // Clear the admin route flag since user is not authenticated
      sessionStorage.removeItem('current_route');
      navigate('/');
      return;
    }    // Allow all authenticated users to access admin portal
    const userEmail = accounts[0].username || accounts[0].name;
    const isAdmin = true; // Allow all authenticated users
    console.log('Admin check result:', { userEmail, isAdmin: true, note: 'All authenticated users can access admin' });
    
    setIsAdminUser(isAdmin);
    setIsLoading(false);
    
    console.log("Authenticated user confirmed, staying on admin page");
    // Set a flag in sessionStorage to remember we're on admin page 
    // This helps maintain the page state during reloads
    sessionStorage.setItem('current_route', 'admin');
  }, [isAuthenticated, accounts, navigate]);

  // Show loading while checking permissions
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6">Checking permissions...</Typography>
      </Box>
    );
  }

  // If not authenticated or not admin, show nothing while redirecting
  if (!isAuthenticated || !isAdminUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6">Access denied. Redirecting...</Typography>
      </Box>
    );
  }

  return (
    <AdminPortal />
  );
}

export default function App() {
  const muiTheme = useTheme();
  // More granular screen size detection
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between("sm", "md"));
  const [mode, setMode] = useState("light");
  const [sessionCounter, setSessionCounter] = useState(1);

  // Pure API-based session management - no localStorage
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const { instance, accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const userName = accounts.length > 0 ? accounts[0].name || accounts[0].username : "";  // Load sessions from API when authenticated
  useEffect(() => {
    const loadSessions = async () => {
      setSessionsLoading(true);
      try {
        if (isAuthenticated) {
          // Get authentication token first
          const token = await apiSessionManager.getAuthToken(instance, accounts, loginRequest);
          
          if (token) {
            const loadedSessions = await apiSessionManager.getSessions(token);
            
            if (loadedSessions.length > 0) {
              setSessions(loadedSessions);
            } else {
              // Create initial temp session if no sessions exist
              const tempSession = apiSessionManager.createTempSession(userName);
              setSessions([tempSession]);
            }
          } else {
            console.warn('Failed to get authentication token, creating temp session');
            const tempSession = apiSessionManager.createTempSession(userName);
            setSessions([tempSession]);
          }
        } else {
          // Not authenticated, create temp session
          const tempSession = apiSessionManager.createTempSession();
          setSessions([tempSession]);
        }
      } catch (error) {
        console.error('Error loading sessions:', error);
        // Fallback to creating a new temp session
        const tempSession = apiSessionManager.createTempSession(userName);
        setSessions([tempSession]);
      } finally {
        setSessionsLoading(false);
      }
    };

    loadSessions();
  }, [isAuthenticated, accounts, instance, userName]);

  // Clear cache when user signs out
  useEffect(() => {
    if (!isAuthenticated) {
      apiSessionManager.clearCache();
    }
  }, [isAuthenticated]);

  // Show loading state while sessions are loading
  if (sessionsLoading) {
    return (
      <ThemeProvider theme={mode === "dark" ? theme : lightTheme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading your conversations...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }
  return (
    <Router>
      <AppContent
        sessions={sessions}
        setSessions={setSessions}
        sessionCounter={sessionCounter}
        setSessionCounter={setSessionCounter}
        isSmallScreen={isSmallScreen}
        isTablet={isTablet}
        mode={mode}
        setMode={setMode}
        sessionsLoading={sessionsLoading}
      />
    </Router>
  );
}

// New component to use Router hooks within Router context
function AppContent({
  sessions,
  setSessions,
  sessionCounter,
  setSessionCounter,
  isSmallScreen,
  isTablet,
  mode,
  setMode,
  sessionsLoading
}) {
  const location = useLocation();

  // State to track if any chat is currently loading
  const [isAnyChatLoading, setIsAnyChatLoading] = useState(false);

  // Check if we're reloading from the admin page 
  useEffect(() => {
    const currentRoute = sessionStorage.getItem('current_route');
    // If we're reloading the admin page, make sure we stay there
    if (currentRoute === 'admin' && location.pathname === '/admin') {
      console.log('Detected admin page reload - maintaining admin route');
    }
  }, [location.pathname]);

  return (
    <ThemeProvider theme={mode === "dark" ? theme : lightTheme}>
      <CssBaseline />
      <Routes>
        <Route path="/" element={
          <PathBasedRedirect
            defaultPath={`/conversation/${sessions[0]?.id || `temp-${Date.now()}`}`}
          />
        } />
        <Route
          path="/conversation/:id"
          element={
            <ChatController
              sessions={sessions}
              setSessions={setSessions}
              sessionCounter={sessionCounter}
              setSessionCounter={setSessionCounter}
              isSmallScreen={isSmallScreen}
              isTablet={isTablet}
              mode={mode}
              setMode={setMode}
              sessionsLoading={sessionsLoading}
              onChatLoadingChange={setIsAnyChatLoading}
            />
          }
        />
        {/* Admin Portal Route with special handling */}
        <Route
          path="/admin"
          element={
            <AdminRouter
              mode={mode}
            />
          }
        />
        {/* Add a web.config file and ensure redirects are working */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
}



// Component to handle path-based redirection with authentication checks
function PathBasedRedirect({ defaultPath }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;

  useEffect(() => {
    console.log('PathBasedRedirect - Current pathname:', location.pathname);
    
    // Don't redirect if we're already on admin path
    if (location.pathname === "/admin") {
      console.log('Already on admin path, setting admin flag');
      sessionStorage.setItem('current_route', 'admin');
      return;
    }

    // Only handle root or unhandled routes
    if (location.pathname === "/" ||
      (!location.pathname.startsWith("/conversation/") && location.pathname !== "/admin")) {

      console.log('Handling root/unhandled route');
      
      // Check if we previously were on admin page
      const wasOnAdminPage = sessionStorage.getItem('current_route') === 'admin';
      if (wasOnAdminPage && isAuthenticated && accounts.length > 0) {
        console.log('Was on admin page, user is authenticated, redirecting back to admin');
        navigate('/admin', { replace: true });
        return;
      } else if (wasOnAdminPage) {
        console.log('Was on admin but not authenticated, clearing admin flag');
        sessionStorage.removeItem('current_route');
      }

      // Try to get the last active session from localStorage first
      let redirectPath = defaultPath;
      try {
        const storedSessionId = localStorage.getItem(CURRENT_SESSION_KEY);
        if (storedSessionId) {
          redirectPath = `/conversation/${storedSessionId}`;
          console.log('PathBasedRedirect: Using stored session from localStorage:', storedSessionId);
        }
      } catch (error) {
        console.warn('PathBasedRedirect: Failed to read stored session from localStorage:', error);
      }

      // Default case - go to conversation (either stored or default)
      console.log('Redirecting to path:', redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [location.pathname, navigate, defaultPath, isAuthenticated, accounts]);

  return null;
}
